/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Game domain - Parallax star field rendering system
 * stars.client.ts - Star background generation and parallax rendering with twinkling effects
 */

import type { Vec2 } from "@game/core";

export interface Star {
  pos: Vec2;
  layer: number;
  speed: number;
  size: number;
  opacity: number;
  twinkle: number;
}

export interface StarLayer {
  count: number;
  speed: number;
  size: number;
  opacity: number;
}

export interface ParallaxCamera {
  pos: Vec2;
}

export interface CanvasDimensions {
  width: number;
  height: number;
}

export interface StarFieldConfig {
  layers: StarLayer[];
  twinkleSpeed: number;
  distributionMultiplier: number;
  cullMargin: number;
}

const DEFAULT_STAR_CONFIG: StarFieldConfig = {
  layers: [
    { count: 200, speed: 0.1, size: 1, opacity: 0.3 },
    { count: 150, speed: 0.3, size: 1.5, opacity: 0.5 },
    { count: 100, speed: 0.7, size: 2, opacity: 0.8 },
  ],
  twinkleSpeed: 0.02,
  distributionMultiplier: 3,
  cullMargin: 50,
};

let starFieldConfig = DEFAULT_STAR_CONFIG;

export function configureStarField(config: Partial<StarFieldConfig>): void {
  starFieldConfig = {
    ...DEFAULT_STAR_CONFIG,
    ...config,
    layers: config.layers || DEFAULT_STAR_CONFIG.layers,
  };
}

export function generateStars(canvasDimensions: CanvasDimensions): Star[] {
  const stars: Star[] = [];

  starFieldConfig.layers.forEach((layer, layerIndex) => {
    for (let i = 0; i < layer.count; i++) {
      stars.push({
        pos: {
          x:
            Math.random() *
            canvasDimensions.width *
            starFieldConfig.distributionMultiplier,
          y:
            Math.random() *
            canvasDimensions.height *
            starFieldConfig.distributionMultiplier,
        },
        layer: layerIndex,
        speed: layer.speed,
        size: layer.size,
        opacity: layer.opacity,
        twinkle: Math.random() * Math.PI * 2,
      });
    }
  });

  return stars;
}

export function calculateStarParallaxPosition(
  star: Star,
  parallaxCamera: ParallaxCamera,
): Vec2 {
  const parallaxX = -parallaxCamera.pos.x * star.speed;
  const parallaxY = -parallaxCamera.pos.y * star.speed;

  return {
    x: star.pos.x + parallaxX,
    y: star.pos.y + parallaxY,
  };
}

export function wrapStarCoordinates(
  pos: Vec2,
  canvasDimensions: CanvasDimensions,
): Vec2 {
  const margin = Math.max(canvasDimensions.width, canvasDimensions.height);

  const wrappedX =
    (((pos.x % (canvasDimensions.width + margin * 2)) +
      (canvasDimensions.width + margin * 2)) %
      (canvasDimensions.width + margin * 2)) -
    margin;

  const wrappedY =
    (((pos.y % (canvasDimensions.height + margin * 2)) +
      (canvasDimensions.height + margin * 2)) %
      (canvasDimensions.height + margin * 2)) -
    margin;

  return { x: wrappedX, y: wrappedY };
}

export function shouldCullStar(
  pos: Vec2,
  canvasDimensions: CanvasDimensions,
): boolean {
  const margin = starFieldConfig.cullMargin;

  return (
    pos.x < -margin ||
    pos.x > canvasDimensions.width + margin ||
    pos.y < -margin ||
    pos.y > canvasDimensions.height + margin
  );
}

export function updateStarTwinkle(star: Star): number {
  star.twinkle += starFieldConfig.twinkleSpeed;
  return star.opacity * (0.8 + 0.2 * Math.sin(star.twinkle));
}

export function renderStars(
  stars: Star[],
  parallaxCamera: ParallaxCamera,
  canvasDimensions: CanvasDimensions,
  ctx: CanvasRenderingContext2D,
): void {
  for (const star of stars) {
    const parallaxPos = calculateStarParallaxPosition(star, parallaxCamera);
    const wrappedPos = wrapStarCoordinates(parallaxPos, canvasDimensions);

    if (shouldCullStar(wrappedPos, canvasDimensions)) {
      continue;
    }

    const twinkleOpacity = updateStarTwinkle(star);

    ctx.fillStyle = `rgba(255, 255, 255, ${twinkleOpacity})`;
    ctx.beginPath();
    ctx.arc(wrappedPos.x, wrappedPos.y, star.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function getStarFieldConfig(): StarFieldConfig {
  return { ...starFieldConfig };
}

export function resetStarFieldConfig(): void {
  starFieldConfig = { ...DEFAULT_STAR_CONFIG };
}

export function getStarFieldStats(stars: Star[]): {
  totalStars: number;
  starsByLayer: Record<number, number>;
  config: StarFieldConfig;
} {
  const starsByLayer: Record<number, number> = {};

  stars.forEach((star) => {
    starsByLayer[star.layer] = (starsByLayer[star.layer] || 0) + 1;
  });

  return {
    totalStars: stars.length,
    starsByLayer,
    config: { ...starFieldConfig },
  };
}
